import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Tone = "primary" | "up" | "down" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export function Button({
  tone = "secondary",
  size = "md",
  busy = false,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone; size?: Size; busy?: boolean; block?: boolean }) {
  const classes = ["btn", `btn-${tone}`, `btn-${size}`, block ? "btn-block" : "", busy ? "is-busy" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={classes} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy && <span className="spinner" aria-hidden />}
      <span className="btn-label">{children}</span>
    </button>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  label,
  className,
}: {
  options: { value: T; label: ReactNode; tone?: "up" | "down" }[];
  value: T;
  onChange: (value: T) => void;
  size?: Size;
  label: string;
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={`seg seg-${size} ${className ?? ""}`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={`seg-item ${option.tone ? `seg-${option.tone}` : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A decimal typed as text: digits and one point, never a float. */
export function AmountInput({
  value,
  onChange,
  prefix,
  suffix,
  placeholder = "0.00",
  label,
  places = 18,
  size = "lg",
  onEnter,
  autoFocus,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  prefix?: ReactNode;
  suffix?: ReactNode;
  placeholder?: string;
  label: string;
  places?: number;
  size?: "md" | "lg";
  onEnter?: () => void;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const id = useId();
  return (
    <div className={`amount amount-${size}`}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      {prefix && <span className="amount-affix">{prefix}</span>}
      <input
        id={id}
        ref={inputRef}
        className="amount-input num"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        onChange={(event) => {
          const text = event.target.value.replace(/,/g, ".").replace(/[^\d.]/g, "");
          const [whole = "", ...rest] = text.split(".");
          const fraction = rest.join("").slice(0, places);
          onChange(rest.length ? `${whole}.${fraction}` : whole);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && onEnter) {
            event.preventDefault();
            onEnter();
          }
        }}
      />
      {suffix && <span className="amount-affix amount-suffix">{suffix}</span>}
    </div>
  );
}

export function Panel({
  title,
  actions,
  children,
  className,
  flush = false,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  flush?: boolean;
}) {
  return (
    <section className={`panel ${flush ? "panel-flush" : ""} ${className ?? ""}`}>
      {(title || actions) && (
        <header className="panel-head">
          {title && <h2 className="panel-title">{title}</h2>}
          {actions && <div className="panel-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Stat({
  label,
  value,
  sub,
  tone,
  size = "md",
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "up" | "down" | "flat" | "warn" | undefined;
  size?: "sm" | "md" | "lg";
}) {
  return (
    <div className={`stat stat-${size}`}>
      <div className="label">{label}</div>
      <div className={`stat-value num ${tone ?? ""}`}>{value}</div>
      {sub !== undefined && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/** How near a position is to its bust: 0 at entry, 10000 at the bust. */
export function RiskMeter({ bp }: { bp: number | null }) {
  if (bp === null) return <span className="faint">frozen</span>;
  const width = Math.min(100, Math.max(0, bp / 100));
  const level = bp >= 8000 ? "high" : bp >= 5000 ? "mid" : "low";
  return (
    <span className={`risk risk-${level}`} title={`${(bp / 100).toFixed(0)}% of the way to bust`}>
      <span className="risk-fill" style={{ width: `${width}%` }} />
    </span>
  );
}

export function Pill({ children, tone }: { children: ReactNode; tone?: "up" | "down" | "warn" | "accent" }) {
  return <span className={`pill ${tone ? `pill-${tone}` : ""}`}>{children}</span>;
}

export function Dot({ tone }: { tone: "up" | "down" | "warn" | "off" }) {
  return <span className={`dot dot-${tone}`} aria-hidden />;
}

export function Skeleton({ width = "100%", height = 16 }: { width?: number | string; height?: number }) {
  return <span className="skeleton" style={{ width, height }} aria-hidden />;
}

export function Empty({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {body && <div className="empty-body">{body}</div>}
      {action}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="tabs">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          role="tab"
          type="button"
          aria-selected={tab.value === value}
          className="tab"
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function CoinMark({ symbol, size = 20 }: { symbol: string; size?: number }) {
  return (
    <span className={`coin coin-${symbol.toLowerCase()}`} style={{ width: size, height: size, fontSize: size * 0.5 }} aria-hidden>
      {symbol.slice(0, 1)}
    </span>
  );
}

/** A dialog: a bottom sheet on phones, centered from 640px. */
export function Sheet({
  open,
  onClose,
  title,
  children,
  side = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  side?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const focusable = ref.current?.querySelector<HTMLElement>("input, button:not(.sheet-close)");
    focusable?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className={`scrim ${side ? "scrim-side" : ""}`} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div ref={ref} className={`sheet ${side ? "sheet-side" : ""}`} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
        <header className="sheet-head">
          <h2 className="sheet-title">{title}</h2>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/** Copies `text`, and says so for a moment. */
export function Copy({ text, children }: { text: string; children: ReactNode }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          // clipboard blocked: nothing to say
        }
      }}
      title="Copy"
    >
      {children}
      <span className="copy-hint">{done ? "copied" : "copy"}</span>
    </button>
  );
}

export function Row({ label, children, tone }: { label: ReactNode; children: ReactNode; tone?: string | undefined }) {
  return (
    <div className="kv">
      <span className="kv-label">{label}</span>
      <span className={`kv-value num ${tone ?? ""}`}>{children}</span>
    </div>
  );
}
